import { Component } from '@theme/component';

/**
 * Tabs (WAI-ARIA Tabs Pattern).
 * Roving tabindex, arrow navigation, Home/End, click activation.
 *
 * @typedef {Object} ThemeTabsRefs
 * @property {HTMLElement} tablist
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 */

/** @extends {Component<ThemeTabsRefs>} */
class ThemeTabs extends Component {
  requiredRefs = ['tablist'];

  /** @type {number} */
  #active = 0;

  /** @type {AbortController | null} */
  #abort = null;

  updatedCallback() {
    super.updatedCallback();
    const { tabs } = this.refs;
    if (!tabs?.length) return;
    this.#active = Math.max(
      0,
      tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true')
    );
    this.#activate(this.#active, false);
  }

  connectedCallback() {
    super.connectedCallback();
    const { tabs, tablist } = this.refs;
    if (!tablist || !tabs?.length) return;

    this.#active = Math.max(
      0,
      tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true')
    );

    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };

    tablist.addEventListener('keydown', (e) => this.#onTablistKeydown(/** @type {KeyboardEvent} */ (e)), opts);

    for (const [i, tab] of tabs.entries()) {
      tab.addEventListener('click', (e) => this.#onTabClick(/** @type {MouseEvent} */ (e), i), opts);
    }

    this.#activate(this.#active, false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  /**
   * @param {number} fromIndex
   * @param {number} direction -1 or +1
   * @returns {number}
   */
  #nextEnabledIndex(fromIndex, direction) {
    const { tabs } = this.refs;
    if (!tabs?.length) return fromIndex;
    let idx = fromIndex;
    for (let c = 0; c < tabs.length; c++) {
      idx = (idx + direction + tabs.length) % tabs.length;
      if (tabs[idx]?.getAttribute('aria-disabled') !== 'true') return idx;
    }
    return fromIndex;
  }

  /** @returns {number} */
  #firstEnabledIndex() {
    const { tabs } = this.refs;
    if (!tabs) return 0;
    const i = tabs.findIndex((t) => t.getAttribute('aria-disabled') !== 'true');
    return i >= 0 ? i : 0;
  }

  /** @returns {number} */
  #lastEnabledIndex() {
    const { tabs } = this.refs;
    if (!tabs) return 0;
    for (let i = tabs.length - 1; i >= 0; i--) {
      if (tabs[i]?.getAttribute('aria-disabled') !== 'true') return i;
    }
    return 0;
  }

  /**
   * @param {KeyboardEvent} event
   */
  #onTablistKeydown(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    if (target.getAttribute('role') !== 'tab') return;

    const { tabs } = this.refs;
    if (!tabs?.length) return;

    const vertical = this.dataset.orientation === 'vertical';
    const prevKey = vertical ? 'ArrowUp' : 'ArrowLeft';
    const nextKey = vertical ? 'ArrowDown' : 'ArrowRight';

    const i = tabs.indexOf(/** @type {HTMLButtonElement} */ (target));
    if (i < 0) return;

    if (event.key === prevKey) {
      event.preventDefault();
      const next = this.#nextEnabledIndex(i, -1);
      tabs[next]?.focus();
      this.#activate(next);
      return;
    }

    if (event.key === nextKey) {
      event.preventDefault();
      const next = this.#nextEnabledIndex(i, 1);
      tabs[next]?.focus();
      this.#activate(next);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      const next = this.#firstEnabledIndex();
      tabs[next]?.focus();
      this.#activate(next);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      const next = this.#lastEnabledIndex();
      tabs[next]?.focus();
      this.#activate(next);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#activate(i);
    }
  }

  /**
   * @param {MouseEvent} event
   * @param {number} index
   */
  #onTabClick(event, index) {
    const tab = /** @type {HTMLButtonElement} */ (event.currentTarget);
    if (tab.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    this.#activate(index);
  }

  /**
   * @param {number} index
   * @param {boolean} [focusTab]
   */
  #activate(index, focusTab = true) {
    const { tabs, panels } = this.refs;
    if (!tabs?.length) return;

    let resolved = index;
    if (tabs[resolved]?.getAttribute('aria-disabled') === 'true') {
      resolved = tabs.findIndex((t) => t.getAttribute('aria-disabled') !== 'true');
      if (resolved < 0) return;
    }

    this.#active = resolved;

    for (const [i, tab] of tabs.entries()) {
      const selected = i === resolved;
      const disabled = tab.getAttribute('aria-disabled') === 'true';
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      tab.tabIndex = disabled ? -1 : selected ? 0 : -1;
      if (panels?.[i]) {
        if (disabled) {
          panels[i].hidden = true;
        } else {
          panels[i].hidden = !selected;
        }
      }
    }

    if (focusTab) {
      tabs[resolved]?.focus();
    }
  }
}

customElements.define('theme-tabs', ThemeTabs);
